/**
 * Date Formatting Utility
 * Automatically formats dates based on user's browser locale
 */

/**
 * Get user's locale from browser, sanitised for Intl APIs.
 * Strips non-BCP-47 extensions (e.g. "en-US@posix") some Linux browsers
 * emit, which would otherwise throw RangeError from Intl.DateTimeFormat.
 */
export const getUserLocale = () => {
  const raw = navigator.language || navigator.userLanguage || 'en-GB';
  // Drop any @extension and keep only the first valid BCP-47 tag segment
  return String(raw).split('@')[0] || 'en-GB';
};

/**
 * Safely build an Intl.DateTimeFormat; falls back to 'en-GB' if the
 * browser-provided locale is invalid.
 */
const safeDateTimeFormat = (locale, options) => {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options);
  }
};

/**
 * Format date based on user's locale
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const locale = getUserLocale();
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return safeDateTimeFormat(locale, defaultOptions).format(dateObj);
};

/**
 * Format date with time
 */
export const formatDateTime = (date, options = {}) => {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit',
    ...options
  });
};

/**
 * Format date as "short" (e.g., 15/01/2025 or 1/15/2025)
 */
export const formatDateShort = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
};

/**
 * Format date as "medium" (e.g., 15 Jan 2025)
 */
export const formatDateMedium = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date as "long" (e.g., 15 January 2025)
 */
export const formatDateLong = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format relative time (e.g., "2 days ago")
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
};

/**
 * Format time range (e.g., "9:00 AM - 5:00 PM")
 */
export const formatTimeRange = (startDate, endDate) => {
  const locale = getUserLocale();
  const options = {
    hour: '2-digit',
    minute: '2-digit'
  };
  
  const start = safeDateTimeFormat(locale, options).format(new Date(startDate));
  const end = safeDateTimeFormat(locale, options).format(new Date(endDate));
  
  return `${start} - ${end}`;
};

/**
 * Get localized month names
 */
export const getMonthNames = (format = 'long') => {
  const locale = getUserLocale();
  const formatter = safeDateTimeFormat(locale, { month: format });
  
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    return formatter.format(date);
  });
};

/**
 * Get localized day names
 */
export const getDayNames = (format = 'long') => {
  const locale = getUserLocale();
  const formatter = safeDateTimeFormat(locale, { weekday: format });
  
  // Start from Sunday (day 0)
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(2000, 0, i + 2); // Jan 2, 2000 was a Sunday
    return formatter.format(date);
  });
};

/**
 * Detect user's date format preference (MM/DD/YYYY or DD/MM/YYYY)
 */
export const getDateFormatPattern = () => {
  const locale = getUserLocale();
  const formatter = safeDateTimeFormat(locale);
  const parts = formatter.formatToParts(new Date(2000, 11, 31));
  
  let pattern = '';
  parts.forEach(part => {
    if (part.type === 'month') pattern += 'MM';
    else if (part.type === 'day') pattern += 'DD';
    else if (part.type === 'year') pattern += 'YYYY';
    else if (part.type === 'literal') pattern += part.value;
  });
  
  return pattern;
};

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};
